import { Button, Group, Modal, Stack } from '@mantine/core'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactCrop, { type Crop, centerCrop, makeAspectCrop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'

const CROP_ASPECT = 1
const MIN_CROP_SIZE = 50

interface ImageCropModalProps {
  opened: boolean
  imageSrc: string
  onClose: () => void
  onCrop: (base64: string) => void
}

function createInitialCrop(width: number, height: number): Crop {
  return centerCrop(
    makeAspectCrop({ unit: '%', width: 80 }, CROP_ASPECT, width, height),
    width,
    height,
  )
}

/**
 * Modal that shows a square crop interface for avatar images.
 * Forces 1:1 aspect ratio. Returns cropped base64 via onCrop callback.
 */
export function ImageCropModal({ opened, imageSrc, onClose, onCrop }: ImageCropModalProps) {
  const { t } = useTranslation()
  const imgRef = useRef<HTMLImageElement | null>(null)
  const [crop, setCrop] = useState<Crop>()

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = e.currentTarget
    imgRef.current = e.currentTarget
    setCrop(createInitialCrop(naturalWidth, naturalHeight))
  }, [])

  function handleSave() {
    const image = imgRef.current
    if (!image || !crop) return

    const canvas = document.createElement('canvas')
    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height
    const pixelX = (crop.x ?? 0) * scaleX
    const pixelY = (crop.y ?? 0) * scaleY
    const pixelW = (crop.width ?? 0) * scaleX
    const pixelH = (crop.height ?? 0) * scaleY

    canvas.width = pixelW
    canvas.height = pixelH
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.drawImage(image, pixelX, pixelY, pixelW, pixelH, 0, 0, pixelW, pixelH)
    onCrop(canvas.toDataURL('image/png'))
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t('my_profile.crop_title')} centered size='md'>
      <Stack spacing='md'>
        {imageSrc && (
          <ReactCrop
            crop={crop}
            onChange={c => setCrop(c)}
            aspect={CROP_ASPECT}
            minWidth={MIN_CROP_SIZE}
            minHeight={MIN_CROP_SIZE}
          >
            {/* eslint-disable-next-line jsx-a11y/alt-text */}
            <img src={imageSrc} onLoad={onImageLoad} style={{ maxWidth: '100%' }} />
          </ReactCrop>
        )}
        <Group position='right'>
          <Button variant='default' onClick={onClose}>{t('my_profile.cancel')}</Button>
          <Button color='turquoise' onClick={handleSave}>{t('my_profile.crop_save')}</Button>
        </Group>
      </Stack>
    </Modal>
  )
}
